import { useState } from "react";
import UI from "@mile-meeting/mile-ui";
import AlertModal from "uis/modal/alert-modal";
import { todayRoomNotificationSettingsService } from "../../pages/meeting/setting-alarm/services";
import { useToast } from "hook/useToast";
import { MainPolicySettingBlock, PolicyCustomizeSection, SubPolicySettingBlock } from "uis/Section";
import React from "react";
import styled from "@emotion/styled";
import { MEETING_ALARM_TARGET } from "types/meeting-settings.types";

//* 당일 회의실 이용 확인 알림 설정 UI
const TodayRoomNotificationSetting = ({
  isActive,
  time,
  alarmSender,
}: {
  isActive: boolean;
  time: string;
  alarmSender: MEETING_ALARM_TARGET;
}) => {
  const [isActiveTodayRoomNotification, setIsActiveTodayRoomNotification] = useState<boolean>(isActive);
  const [todayRoomNotificationTime, setTodayRoomNotificationTime] = useState<string>(time);
  const [todayRoomNotificationTarget, setTodayRoomNotificationTarget] = useState<MEETING_ALARM_TARGET>(alarmSender);
  const [openInActiveModal, setOpenInActiveModal] = useState<boolean>(false);
  const [openActiveModal, setOpenActiveModal] = useState<boolean>(false);
  const { showToast } = useToast();
  const timeDate = [
    `06:00`,
    `06:30`,
    `07:00`,
    `07:30`,
    `08:00`,
    `08:30`,
    `09:00`,
    `09:30`,
    `10:00`,
    `10:30`,
    `11:00`,
    `11:30`,
    `12:00`,
  ];

  //* 당일 회의실 이용 확인 알림 활성화 가이드 페이지 열기
  const openGuide = () => {
    window.open(
      "https://mile-corporation.notion.site/12ea222a9c1b819b9c04cac8e4f1d746#232a222a9c1b8085993af4f140d32fb1"
    );
  };

  //* 당일 회의실 이용 확인 알림 활성화 변경
  const onHandleChangeActivate = (value: boolean) => {
    if (isActiveTodayRoomNotification) {
      setOpenInActiveModal(true);
    } else if (!isActiveTodayRoomNotification) {
      setOpenActiveModal(true);
    }
  };

  return (
    <PolicyCustomizeSection
      title="당일 회의실 이용 확인 알림 설정"
      rightChildren={
        <UI.ButtonGroups.Text title="당일 회의실 이용 확인 알림 더 알아보기" size="small" onClick={openGuide} />
      }
    >
      <MainPolicySettingBlock
        title="당일 회의실 이용 확인 알림 활성화"
        description="활성화 시 설정된 시간에 워크스페이스 전 멤버에게 당일 회의실 예약 내역 및 이용 여부 확인 알림을 전송합니다."
        haveChildren={isActiveTodayRoomNotification}
        rightChildren={
          <UI.Selection.Toggle.Default
            state={isActiveTodayRoomNotification}
            onChange={onHandleChangeActivate}
            size="large"
          />
        }
      />
      {isActiveTodayRoomNotification && (
        <SubPolicySettingBlock
          styles={{
            padding: "12px 28px 12px 40px",
          }}
          title="당일 회의실 이용 확인 알림 전송 시간"
          description="해당 일자에 예약 및 참석 회의가 있는 전 멤버에게 설정한 시간에 당일 회의실 이용 확인 알림을 전송합니다."
          rightChildren={
            <UI.SelectBox
              size="small"
              data={[...timeDate.map((time) => `${time}`)]}
              selectedData={[todayRoomNotificationTime]}
              width={120}
              dropdownMaxHeight={120}
              onChange={(time: string[]) =>
                todayRoomNotificationSettingsService.onChangeTime(time[0], setTodayRoomNotificationTime, showToast)
              }
            />
          }
        />
      )}
      {isActiveTodayRoomNotification && (
        <SubPolicySettingBlock
          title="당일 회의실 이용 확인 알림 수신 대상"
          description="설정된 대상(워크스페이스 멤버 한정)에게 당일 회의실 이용 확인 알림을 전송합니다."
          rightChildren={
            <ChannelSettingContainer>
              <RadioContainer
                onClick={() => {
                  if (todayRoomNotificationTarget === MEETING_ALARM_TARGET.ALL) return;
                  todayRoomNotificationSettingsService.onChangeTarget(
                    MEETING_ALARM_TARGET.ALL,
                    setTodayRoomNotificationTarget,
                    showToast
                  );
                }}
              >
                <UI.Radio selected={todayRoomNotificationTarget === MEETING_ALARM_TARGET.ALL} />
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  예약자 및 참석자 전체
                </UI.Typography>
              </RadioContainer>
              <RadioContainer
                onClick={() => {
                  if (todayRoomNotificationTarget === MEETING_ALARM_TARGET.CREATOR) return;
                  todayRoomNotificationSettingsService.onChangeTarget(
                    MEETING_ALARM_TARGET.CREATOR,
                    setTodayRoomNotificationTarget,
                    showToast
                  );
                }}
              >
                <UI.Radio selected={todayRoomNotificationTarget === MEETING_ALARM_TARGET.CREATOR} />
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  예약자만
                </UI.Typography>
              </RadioContainer>
            </ChannelSettingContainer>
          }
        />
      )}
      <AlertModal
        title={
          <p>
            당일 회의실 이용 확인 알림을
            <br />
            비활성화하시겠습니까?
          </p>
        }
        btnTitle="변경하기"
        content={`당일 회의실 이용 확인 알림을 전송하지 않습니다.`}
        onChange={() =>
          todayRoomNotificationSettingsService.onChangeActivate(
            false,
            setIsActiveTodayRoomNotification,
            setOpenInActiveModal,
            showToast
          )
        }
        modalIsOpen={openInActiveModal}
        closeModal={() => setOpenInActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            당일 회의실 이용 확인 알림을
            <br />
            활성화하시겠습니까?
          </p>
        }
        btnTitle="변경하기"
        content={
          <p style={{ textAlign: "center" }}>
            해당 일자에 예약 및 참석 회의가 있는 전 멤버에게
            <br />
            회의실 예약 내역 및 이용 여부 확인 알림을 전송합니다.
          </p>
        }
        onChange={() =>
          todayRoomNotificationSettingsService.onChangeActivate(
            true,
            setIsActiveTodayRoomNotification,
            setOpenActiveModal,
            showToast
          )
        }
        modalIsOpen={openActiveModal}
        closeModal={() => setOpenActiveModal(false)}
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(TodayRoomNotificationSetting);

const ChannelSettingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
